import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/v2/components/ui/dialog";
import { Button } from "@/v2/components/ui/button";
import type { SecondScreenCode } from "@/Pages/Watch/VideoPlayerLive/useSecondScreenSession";

type Props = {
  open: boolean;
  /** Set once the API has minted a code; null while that request is in flight. */
  code: SecondScreenCode | null;
  onCancel: () => void;
};

/**
 * The three ways a fan can read this dialog: what it wants them to do, in
 * order. Rendered as plain rows rather than a semantic <ol>, because v2.css
 * resets list-style to none on every <ol>/<ul> (see the .v2 list reset), so
 * the browser's own numbering would not have shown up anyway.
 */
const STEPS = [
  "Scan the QR code using your phone's camera.",
  // No leading "Or": the numbered rows already read as alternatives, and the
  // word made this look like a continuation of step 1 rather than the other
  // way in. "displayed above" points at the PIN, which sits above these rows.
  "Enter the 4-digit PIN displayed above.",
  // The payoff, rather than the mechanism. This used to say the screen
  // "steps back", which described the implementation (the panel goes inert)
  // instead of what the fan actually gets: the whole screen for the game, and
  // the layer in their hand.
  "Enjoy the game in full screen and access Community and Moments right from your phone.",
];

/**
 * The "hand this to my phone" dialog: a QR code and a PIN a fan can use on
 * their phone to take over the Audience Layer for this game. Opened from the
 * widget's own second-screen button (see useAudienceLayerHost's `secondScreen`
 * context) and driven entirely by Task 9's useSecondScreenSession.
 *
 * qrSvg is rendered with dangerouslySetInnerHTML: it is a server-generated SVG
 * from our own API, and the alternative is shipping a QR library to every fan
 * who never pairs a phone.
 */
export function PairPhoneDialog({ open, code, onCancel }: Props) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Use the Audience Layer on Your Phone</DialogTitle>
          <DialogDescription>Keep this page open until pairing finishes.</DialogDescription>
        </DialogHeader>

        {code ? (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <div
                className="shrink-0 rounded-lg border border-border bg-card"
                style={{ padding: "8px" }}
                // See the module comment: server-generated SVG, own API.
                dangerouslySetInnerHTML={{ __html: code.qrSvg }}
              />
              <div className="flex flex-col gap-2">
                {/* No leading "Or". This label sits beside the QR code, not
                    after a sentence, so the word had nothing to be an
                    alternative to and read as a fragment. Step 2 below points
                    at these digits and carries the choice instead. */}
                <span className="text-xs text-muted-foreground">Type this PIN</span>
                <div className="flex gap-1.5">
                  {code.pin.split("").map((digit, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-center rounded-md border border-border bg-card text-lg font-semibold text-foreground"
                      style={{ width: 36, height: 44 }}
                    >
                      {digit}
                    </div>
                  ))}
                </div>
                <span className="text-xs text-muted-foreground">
                  One-time use, expires in 10 minutes.
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              {STEPS.map((step, index) => (
                <div key={step} className="flex items-start gap-2">
                  <span
                    className="flex shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground"
                    style={{ width: 20, height: 20 }}
                    aria-hidden="true"
                  >
                    {index + 1}
                  </span>
                  <span className="text-sm text-foreground">{step}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div
            className="flex items-center justify-center"
            style={{ minHeight: 160 }}
            role="status"
            aria-label="Preparing your pairing code"
          >
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" aria-hidden="true" />
            <span className="sr-only">Preparing your pairing code</span>
          </div>
        )}

        <div className="flex justify-end">
          <Button variant="outline" className="ring-1 ring-inset ring-border" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
